import BaseButton from 'components/atoms/baseButton';
import PersonWorkspaceIcon from 'assets/img/person-workspace.svg?react';
import { useEffect, type FC } from 'react';
import BaseTableNew from 'components/atoms/base-table/new';
import {
  useFollowUpTableDataQueryFilter,
  useConsoleTable,
  useGetFollowUpTableData,
  CCM_CONSOLE_PAGE_SIZE,
} from 'infrastructure/hooks/ccm/console';
import { t } from 'i18next';
import { checkIsPageAvailable } from 'infrastructure/hooks/ccm/console/utils';

import { AccountsSelect } from '../select-boxes/select-account';
import CcmConsoleTableBlock from '../table-block';
import s from './styles.module.scss';
import { useColumns } from './columns';
import { NumberOfCallSelectBox } from '../select-boxes/number-of-call';
import { FollowUpCompletedSelect } from '../select-boxes/completed';

import type {
  IFollowUpTableData,
  IFollowUpTableDataFilters,
} from 'infrastructure/interfaces';

interface FollowUpTableProps {}

const FollowUpTable: FC<FollowUpTableProps> = () => {
  const { filter, updateFilter } = useFollowUpTableDataQueryFilter();
  const { data, isLoading } = useGetFollowUpTableData(filter);
  const columns = useColumns({ filter });
  const items = data?.data || [];
  const total = data?.count || 0;

  const { onSort, pushToQueue, rowSelection, disableAddToQueue } =
    useConsoleTable<IFollowUpTableDataFilters, IFollowUpTableData>({
      filter,
      updateFilter,
      tablePrefix: 'f-',
      excludeSortFields: [
        'f-page',
        'f-clinicGuid',
        'f-parentClinicGuid',
        'f-numberOfCalls',
        'f-completed',
        'patientGuid',
      ],
      tableName: 'follow-up',
    });

  useEffect(() => {
    const isPageExist = checkIsPageAvailable(
      filter['f-page'],
      total,
      items.length,
    );
    if (!isPageExist && !isLoading && filter['f-page'] !== 1) {
      updateFilter({ 'f-page': undefined });
    }
  }, [filter['f-page'], total, isLoading, items.length]);

  return (
    <CcmConsoleTableBlock title={t('labels.followUpPatients')} total={total}>
      <div className={s['select-wrapper']}>
        <AccountsSelect
          updateFilter={updateFilter}
          tablePrefix="f-"
          clinicGuid={filter['f-clinicGuid']}
          parentClinicGuid={filter['f-parentClinicGuid']}
        />
        <NumberOfCallSelectBox
          tablePrefix="f-"
          cyPrefix="follow-up"
          value={filter['f-numberOfCalls']}
          updateFilter={updateFilter}
        />
        <FollowUpCompletedSelect
          tablePrefix="f-"
          cyPrefix="follow-up-completed"
          value={filter['f-completed']}
          updateFilter={updateFilter}
        />
        <BaseButton
          label={t('labels.addToWorkQueue')}
          className={s['add-queue-btn']}
          icon={<PersonWorkspaceIcon />}
          onClick={pushToQueue}
          disabled={disableAddToQueue}
        />
      </div>
      <BaseTableNew
        items={items}
        columns={columns}
        bordered
        loading={isLoading}
        pagination={{
          current: filter['f-page'],
          pageSize: CCM_CONSOLE_PAGE_SIZE,
          total,
          position: ['bottomCenter'],
        }}
        onChangePage={(newPage) => {
          updateFilter({ 'f-page': newPage });
        }}
        dataCy="follow-up-patients-table"
        tableRowSelection={rowSelection}
        onSort={onSort}
        className={s.table}
        rowHoverable={false}
      />
    </CcmConsoleTableBlock>
  );
};

export default FollowUpTable;
